import React, { Component } from 'react'
import LoadingSpinner from '../../shared/LoadingSpinner.component'
import {
  MDBBtn,
  MDBModalHeader,
  MDBModalBody,
  MDBModal,
  MDBInput,
  MDBIcon,
  MDBBadge,
  MDBModalDialog,
  MDBModalContent,
  MDBModalTitle,
} from 'mdb-react-ui-kit'
import { toast } from 'react-toastify'
import AdminApi from './../../../api/admin-api/admin-api'
import UserProfileService from './../../../shared/services/UserProfile.service'
import './SupportEmailsModal.component.scss'

const META_KEY = 'profile---support-emails'

class SupportEmailsModal extends Component {
  state = {
    isOpen: false,
    loading: false,
    saving: false,
    emails: [],
    newEmail: '',
    emailError: '',
  }

  _toggle = () => {
    if (this.state.saving) return

    const isOpen = !this.state.isOpen
    this.setState({ isOpen, newEmail: '', emailError: '' })

    if (isOpen) this._fetchEmails()
  }

  _getUserId = () => {
    return this.props.userId || UserProfileService.getUserId()
  }

  _fetchEmails = async () => {
    this.setState({ loading: true })
    try {
      const userId = this._getUserId()
      const response = await AdminApi.getUserMetas(userId, [META_KEY])
      let emails = []
      if (response && response.data && response.data.data) {
        const meta = Array.isArray(response.data.data) ? response.data.data.find(m => m.meta_key === META_KEY) : null
        if (meta && meta.meta_value) {
          try {
            emails = JSON.parse(meta.meta_value)
            if (!Array.isArray(emails)) emails = []
          } catch (e) {
            emails = []
          }
        }
      }
      this.setState({ emails, loading: false })
    } catch (ex) {
      console.error('Failed to fetch support emails', ex)
      this.setState({ emails: [], loading: false })
    }
  }

  _saveEmails = async emails => {
    this.setState({ saving: true })
    try {
      const userId = this._getUserId()
      await AdminApi.upsertUserMeta(userId, META_KEY, JSON.stringify(emails))
      this.setState({ emails, saving: false })
      toast.success('Support emails updated.', {
        position: toast.POSITION.TOP_RIGHT,
      })
    } catch (ex) {
      console.error('Failed to save support emails', ex)
      toast.error('Failed to save support emails.', {
        position: toast.POSITION.TOP_RIGHT,
      })
      this.setState({ saving: false })
    }
  }

  _validateEmail = email => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  _addEmail = () => {
    const email = this.state.newEmail.trim().toLowerCase()

    if (!email) {
      this.setState({ emailError: 'Please enter an email address.' })
      return
    }
    if (!this._validateEmail(email)) {
      this.setState({ emailError: 'Please enter a valid email address.' })
      return
    }
    if (this.state.emails.includes(email)) {
      this.setState({ emailError: 'This email is already in the list.' })
      return
    }

    const updatedEmails = [...this.state.emails, email]
    this.setState({ newEmail: '', emailError: '' })
    this._saveEmails(updatedEmails)
  }

  _removeEmail = email => {
    const updatedEmails = this.state.emails.filter(e => e !== email)
    this._saveEmails(updatedEmails)
  }

  _handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      this._addEmail()
    }
  }

  _isAllowed = UserProfileService.isA(['internal-admin', 'system-admin'], true)

  render() {
    if (!this._isAllowed) return <></>

    const { isOpen, loading, saving, emails, newEmail, emailError } = this.state
    const { buttonLabel, buttonSize, buttonColor, buttonClassName } = this.props

    return (
      <>
        <MDBBtn
          type='button'
          size={buttonSize || ''}
          color={buttonColor || 'info'}
          className={buttonClassName || ''}
          onClick={this._toggle}
        >
          <MDBIcon icon='envelope' className='me-1' />
          &nbsp;{buttonLabel || 'Support Emails'}
        </MDBBtn>

        <MDBModal open={isOpen} onClose={this._toggle}>
          <MDBModalDialog size='md'>
            <MDBModalContent>
              <MDBModalHeader>
                <MDBModalTitle>Manage Support Ticket Emails</MDBModalTitle>
                <MDBBtn type='button' className='btn-close' color='none' onClick={this._toggle}></MDBBtn>
              </MDBModalHeader>
              <MDBModalBody className={loading ? 'is-loading' : ''}>
                {loading ? (
                  <div className='loading-wrapper'>
                    <LoadingSpinner size='md' isActive={true} label='Loading Support Emails' />
                  </div>
                ) : (
                  <div className='support-emails-modal-content'>
                    <p className='text-muted mb-3'>
                      Add additional email addresses that have been used to submit support tickets. These emails will be
                      included when searching for the agent's tickets.
                    </p>

                    <div className='email-input-row'>
                      <MDBInput
                        type='email'
                        label='Add email address'
                        value={newEmail}
                        onChange={e =>
                          this.setState({
                            newEmail: e.target.value,
                            emailError: '',
                          })
                        }
                        onKeyDown={this._handleKeyDown}
                        className={emailError ? 'is-invalid' : ''}
                      />
                      <MDBBtn type='button' size='sm' color='primary' onClick={this._addEmail} disabled={saving}>
                        <MDBIcon icon='plus' />
                      </MDBBtn>
                    </div>
                    {emailError && <div className='text-danger small mb-2'>{emailError}</div>}

                    {emails.length > 0 ? (
                      <div className='email-list'>
                        <label className='font-weight-bold mb-2'>Additional Support Emails:</label>
                        <ul className='list-unstyled'>
                          {emails.map(email => (
                            <li key={email} className='email-list-item'>
                              <MDBBadge color='light' className='email-badge'>
                                <span className='email-text'>{email}</span>
                                <MDBBtn
                                  type='button'
                                  size='sm'
                                  flat
                                  color='default'
                                  className='email-remove-btn p-0 m-0 ml-2 btn-flat'
                                  onClick={() => this._removeEmail(email)}
                                  disabled={saving}
                                >
                                  <MDBIcon icon='times' className='text-danger' />
                                </MDBBtn>
                              </MDBBadge>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className='text-muted text-center mt-3'>No additional support emails configured.</div>
                    )}

                    {saving && (
                      <div className='text-center mt-2'>
                        <LoadingSpinner size='sm' isActive={true} label='Saving...' />
                      </div>
                    )}
                  </div>
                )}
              </MDBModalBody>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>
      </>
    )
  }
}

export default SupportEmailsModal
